// N529526
import styled from '@emotion/styled'
import React, { useEffect } from 'react'
import { Platform } from '@/firebase/firestore/Platform'
import { Sex } from '@/firebase/firestore/Sex'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import Image from 'next/image'
import fitty from 'fitty'
import { useLocale } from '@/hooks/useLocale'

type Props = {
  targetId: string
  templateColor: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7
  uid: string
  previewImage: string
  name: string
  sex: Sex
  platform: Array<Platform>
  voiceChat: Array<VoiceChat>
  playStyle: string
  fontFamily: string
  fontColor: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  playTime: string
  crown: string
  rank: number
  title: string
  experience: string
  favoriteLeader: string
  hitokoto: string
}

export const Pazudora1: React.FC<Props> = ({
  uid,
  previewImage,
  name,
  sex,
  fontFamily,
  fontColor,
  playStyle,
  platform,
  templateColor,
  crown,
  rank,
  title,
  experience,
  favoriteLeader,
  hitokoto,
  targetId,
}) => {
  const { t } = useLocale()
  useEffect(() => {
    fitty('#name', {
      maxSize: 20,
      minSize: 12,
    })
    fitty('#gameId', {
      maxSize: 20,
      minSize: 12,
    })
    fitty('#playStyle', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#playTime', {
      maxSize: 16,
      minSize: 8,
    })
    fitty('#gameMode', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#crown', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#rank', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#title', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#experience', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#favoriteWeapon', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#favoriteLeader', {
      maxSize: 20,
      minSize: 12,
    })
    fitty('#hitokoto', {
      maxSize: 20,
      minSize: 12,
    })
  }, [])
  return (
    <ProfileContainer>
      <ProfileInner id={targetId} fontFamily={fontFamily} fontColor={fontColor}>
        <Image
          width="1200"
          height="900"
          priority={true}
          unoptimized={true}
          src={`/static/images/profiles/pazudora1/${templateColor}-min.jpeg`}
          alt={`パズドラ自己紹介カード ${templateColor}`}
        />
        <PreviewImage src={previewImage} />
        <Name>
          <span id="name">{name}</span>
        </Name>
        <Id>
          <span id="gameId">{uid}</span>
        </Id>
        <Crown>
          <span id="crown">{crown}</span>
        </Crown>
        <Rank>
          <span id="rank">{rank}</span>
        </Rank>
        <Title>
          <span id="title">{title}</span>
        </Title>
        <Experience>
          <span id="experience">{experience}</span>
        </Experience>
        <FavoriteLeader>
          <span id={'favoriteLeader'}>{favoriteLeader}</span>
        </FavoriteLeader>
        <Hitokoto>
          <span id={'hitokoto'}>{hitokoto}</span>
        </Hitokoto>
        <PlayStyle>
          <span id="playStyle">{playStyle}</span>
        </PlayStyle>
      </ProfileInner>
    </ProfileContainer>
  )
}

const ProfileContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: #4c4c4c;
`

const ProfileInner = styled.div<{ fontFamily: string; fontColor: string }>`
  position: relative;
  width: 320px;
  line-height: 0;

  * {
    line-height: 1;
    font-family: ${({ fontFamily }) => fontFamily};
    color: ${({ fontColor }) => fontColor};
    border-color: ${({ fontColor }) => fontColor};
  }
`

const PreviewImage = styled.img`
  position: absolute;
  top: 20.5%;
  left: 5.5%;
  width: 84px;
  height: 84px;
  object-fit: cover;
  border-radius: 50%;
  overflow: hidden;
`

const Name = styled.div`
  position: absolute;
  top: 24%;
  left: 37%;
  width: 25%;
  height: 10%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
  }
`

const Id = styled.div`
  position: absolute;
  top: 24%;
  left: 71%;
  width: 20%;
  height: 10%;
  text-align: center;
  display: flex;
  align-items: center;

  span {
    font-weight: bold;
  }
`

const PlayStyle = styled.div`
  position: absolute;
  top: 39%;
  left: 30%;
  width: 15%;
  height: 10%;
  display: flex;
  justify-content: center;

  span {
    font-weight: bold;
    line-height: 0.9;
  }
`

const Crown = styled.div`
  position: absolute;
  top: 43.5%;
  left: 31.5%;
  text-align: center;
  width: 20%;
  word-break: break-all;
  height: 12%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    text-align: center;
    font-weight: bold;
    line-height: 0.9;
  }
`

const Rank = styled.div`
  position: absolute;
  top: 43.5%;
  left: 47.5%;
  text-align: center;
  width: 20%;
  word-break: break-all;
  height: 12%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    text-align: center;
    font-weight: bold;
    line-height: 0.9;
  }
`

const Title = styled.div`
  position: absolute;
  top: 43.5%;
  left: 64%;
  text-align: center;
  width: 20%;
  word-break: break-all;
  height: 12%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    text-align: center;
    font-weight: bold;
    line-height: 0.9;
  }
`

const Experience = styled.div`
  position: absolute;
  top: 43.5%;
  left: 80%;
  text-align: center;
  width: 20%;
  word-break: break-all;
  height: 12%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    text-align: center;
    font-weight: bold;
    line-height: 0.9;
  }
`

const FavoriteLeader = styled.div`
  position: absolute;
  top: 63%;
  left: 7%;
  height: 24%;
  width: 40%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
    line-height: 1.4;
  }
`

const Hitokoto = styled.div`
  position: absolute;
  top: 63%;
  left: 54%;
  height: 24%;
  width: 40%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
    line-height: 1.4;
  }
`
